import React, { useEffect, useRef } from 'react';
import L from './leafletHeat';
import useDistrictMap from './useDistrictMap';
import palette from '../palette';
import statsStyles from '../stats.module.css';
import styles from './DistrictMap.module.css';
import { formatNumber } from '../../utils/format';

const TILES_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILES_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>';

/** Enquadra os bairros perto do maior (até 60 km): quem mora em outra cidade não afasta o mapa. */
const FOCUS_RADIUS_METERS = 60000;

const HEAT_OPTIONS = {
  radius: 38,
  blur: 28,
  maxZoom: 12,
  minOpacity: 0.3,
  gradient: { 0.2: '#BFE8E1', 0.45: palette.teal, 0.75: palette.amber, 1: palette.red },
};

const NO_POINTS = [];

const patientsText = (count) => `${formatNumber(count)} ${count === 1 ? 'paciente' : 'pacientes'}`;

/** Balão com o nome do bairro como texto (o nome vem do cadastro, nunca vira HTML). */
const tooltipContent = (point) => {
  const element = document.createElement('span');
  const name = document.createElement('strong');
  name.textContent = point.label;
  element.appendChild(name);
  element.appendChild(document.createTextNode(`: ${patientsText(point.count)}`));
  return element;
};

const focusBounds = (points) => {
  const center = L.latLng(points[0].lat, points[0].lng);
  const near = points.filter((point) => center.distanceTo([point.lat, point.lng]) <= FOCUS_RADIUS_METERS);
  return L.latLngBounds(near.map((point) => [point.lat, point.lng]));
};

const footerParts = (map) => {
  const parts = [];
  if (map.notFound > 0) parts.push(`${patientsText(map.notFound)} de bairros que o mapa não encontrou`);
  if (map.withoutDistrict > 0) parts.push(`${patientsText(map.withoutDistrict)} sem bairro no cadastro`);
  return parts;
};

/**
 * Mapa de calor dos bairros dos pacientes atendidos (OpenStreetMap +
 * leaflet.heat), com uma bolinha por bairro e o total no balão. Não entra no
 * PDF: lá o ranking de bairros já resume.
 */
const DistrictMap = ({ initial, period }) => {
  const { map, locating } = useDistrictMap(initial, period);
  const containerRef = useRef(null);
  const leafletRef = useRef(null);
  const layersRef = useRef(null);
  const draggedRef = useRef(false);
  const points = map?.points || NO_POINTS;

  useEffect(() => {
    const leaflet = L.map(containerRef.current, { scrollWheelZoom: false, zoomSnap: 0.5 });
    L.tileLayer(TILES_URL, { attribution: TILES_ATTRIBUTION, maxZoom: 18, className: styles.tiles }).addTo(leaflet);
    leaflet.setView([-15.8, -47.9], 4);
    leaflet.on('dragstart', () => { draggedRef.current = true; });
    leafletRef.current = leaflet;

    const resize = new ResizeObserver(() => leaflet.invalidateSize());
    resize.observe(containerRef.current);
    return () => {
      resize.disconnect();
      leaflet.remove();
      leafletRef.current = null;
    };
  }, []);

  useEffect(() => {
    const leaflet = leafletRef.current;
    if (!leaflet) return;
    if (layersRef.current) layersRef.current.remove();
    if (points.length === 0) return;

    const max = Math.max(...points.map((point) => point.count));
    const group = L.layerGroup();
    L.heatLayer(points.map((point) => [point.lat, point.lng, point.count / max]), HEAT_OPTIONS).addTo(group);
    points.forEach((point) => {
      L.circleMarker([point.lat, point.lng], {
        radius: 5 + 11 * Math.sqrt(point.count / max),
        color: '#FFFFFF',
        weight: 1.5,
        fillColor: palette.teal,
        fillOpacity: 0.55,
      })
        .bindTooltip(tooltipContent(point), { direction: 'top', className: styles.tooltip })
        .addTo(group);
    });
    group.addTo(leaflet);
    layersRef.current = group;

    if (!draggedRef.current) {
      leaflet.fitBounds(focusBounds(points), { padding: [36, 36], maxZoom: 13 });
    }
  }, [points]);

  const footer = footerParts(map || {});
  const empty = points.length === 0 && !locating;

  return (
    <div className={styles.wrap}>
      <div className={styles.mapBox}>
        <div ref={containerRef} className={styles.map} aria-label="Mapa de calor dos bairros dos pacientes" />
        {locating && (
          <span className={styles.status}>
            {`Localizando bairros no mapa${map.pending > 0 ? ` (faltam ${formatNumber(map.pending)})` : ''}...`}
          </span>
        )}
        {empty && (
          <div className={`${statsStyles.emptyChart} ${styles.empty}`}>
            Nenhum bairro dos pacientes atendidos foi encontrado no mapa.
          </div>
        )}
      </div>
      {(points.length > 0 || footer.length > 0) && (
        <span className={styles.footer}>
          {[`${formatNumber(points.length)} ${points.length === 1 ? 'bairro' : 'bairros'} no mapa`, ...footer].join(' · ')}
        </span>
      )}
    </div>
  );
};

export default DistrictMap;
